import {ChangeEvent, FC, useLayoutEffect, useState} from 'react';
//formik
import {Field, useFormikContext} from 'formik';
import {CreateEventFormValues} from '../../formConfiguration';
//mui
import {Box, Tooltip, Typography, IconButton} from '@mui/material';
//constants
import {
  ADD_TICKET_TIER,
  BODY1,
  DELETE_UPPER,
  FIELD_LABEL,
  NUMBER,
  PLACEHOLDER,
  SUBTITLE1,
  TABLE_NAME_TEXT,
  TEXT,
  TOP,
  ZERO,
} from '@/utils/constants';
//components
import Button from '@/shared/components/Button';
import Input from '@/shared/components/Inputs/Input';
//helpers
import {setRequiredLabel} from '@/utils/helpers';
import {initialTableInfos} from './helpers';
//icons
import {ReactComponent as PlusIcon} from '@/assets/icons/plus.svg';
import {ReactComponent as QuestionIcon} from '@/assets/icons/modal-question.svg';
import {ReactComponent as ArrowDownIcon} from '@icons/select-arrow-down.svg';
//styles
import {ticketsTablesStyle} from './TicketsTables.styles';
import {TreeItem, TreeView} from '@mui/x-tree-view';
import Switch from '@/shared/components/Switches/Switch';
import ActionsMenu from '@/shared/components/Table/ActionMenu';
import CurrencyField from '@/shared/components/Inputs/CurrencyInput';

const TicketsTables: FC = () => {
  const {
    setFieldValue,
    values: {tableInfos},
  } = useFormikContext<CreateEventFormValues>();

  useLayoutEffect(() => {
    if (!tableInfos.length) addTableClickHandler();
  }, []);

  const [copiedId, setCopiedId] = useState<number | null>(null);

  const addTableClickHandler = () => {
    const nextId = tableInfos.length ? Math.max(...tableInfos.map((t) => t.id)) + 1 : 1;

    setFieldValue('tableInfos', [...tableInfos, {...initialTableInfos, id: nextId}]);
  };

  const removeTableClickHandler = (id: number) => {
    const tableToRemove = tableInfos.find((table) => table.id === id);
    if (!tableToRemove) return;

    const newTableInfos = tableInfos.filter((table) => table.id !== id);
    setFieldValue('tableInfos', newTableInfos);
  };

  return (
    <Box>
      <Typography variant={SUBTITLE1} sx={ticketsTablesStyle.title}>
        Tables & Tickets
      </Typography>

      <Box>
        {tableInfos.map((table, index) => (
          <Box key={index} sx={{marginBottom: '20px'}}>
            <Box sx={ticketsTablesStyle.content}>
              <Field
                index={index}
                id={`tableInfos[${index}].tier`}
                name={`tableInfos[${index}].tier`}
                placeholder={PLACEHOLDER.ticketsTier}
                label={
                  index === 0 ? (
                    <Box sx={{display: 'flex', alignItems: 'center'}}>
                      <span>{setRequiredLabel(FIELD_LABEL.ticketsTier)}</span>
                      <Tooltip title={TABLE_NAME_TEXT} placement={TOP}>
                        <Box sx={ticketsTablesStyle.coverFeeTooltip}>
                          <QuestionIcon />
                        </Box>
                      </Tooltip>
                    </Box>
                  ) : (
                    setRequiredLabel(FIELD_LABEL.ticketsTier)
                  )
                }
                component={Input}
                sx={ticketsTablesStyle.ticketsTier}
                isArrayData
              />
              <Field
                id={`tableInfos[${index}].amount`}
                name={`tableInfos[${index}].amount`}
                label={
                  index === 0 ? (
                    <Box sx={{display: 'flex', alignItems: 'center'}}>
                      <span>{setRequiredLabel(FIELD_LABEL.numberOfTables)}</span>
                      <Tooltip title="How many of these tickets or tables are you selling?" placement={TOP}>
                        <Box sx={ticketsTablesStyle.coverFeeTooltip}>
                          <QuestionIcon />
                        </Box>
                      </Tooltip>
                    </Box>
                  ) : (
                    setRequiredLabel(FIELD_LABEL.numberOfTables)
                  )
                }
                placeholder={String(ZERO)}
                type={NUMBER}
                component={Input}
                isArrayData
              />
              <Field
                id={`tableInfos[${index}].numberOfSeats`}
                name={`tableInfos[${index}].numberOfSeats`}
                label={
                  index === 0 ? (
                    <Box sx={{display: 'flex', alignItems: 'center'}}>
                      <span>{setRequiredLabel(FIELD_LABEL.numberOfSeats)}</span>
                      <Tooltip title="How many seats come with each table or ticket?" placement={TOP}>
                        <Box sx={ticketsTablesStyle.coverFeeTooltip}>
                          <QuestionIcon />
                        </Box>
                      </Tooltip>
                    </Box>
                  ) : (
                    setRequiredLabel(FIELD_LABEL.numberOfSeats)
                  )
                }
                placeholder={String(ZERO)}
                type={NUMBER}
                component={Input}
                isArrayData
              />

              <CurrencyField
                name={`tableInfos[${index}].price`}
                label={setRequiredLabel(FIELD_LABEL.price)}
                placeholder="0"
                isArrayData
              />

              <CurrencyField
                name={`tableInfos[${index}].fairMarketValue`}
                label={FIELD_LABEL.fairMarketValue}
                placeholder="0"
                isArrayData
              />

              <ActionsMenu
                parentClassName={{marginLeft: '-10px'}}
                disableRipple
                content={
                  <Box>
                    <Typography
                      onClick={() => removeTableClickHandler(table.id)}
                      variant={BODY1}
                      // sx={{
                      //   ...ticketsTablesStyle.menuItem,
                      //   ...(index === 0 && ticketsTablesStyle.disabledMenuItem),
                      // }}
                      sx={ticketsTablesStyle.menuItem}
                    >
                      {DELETE_UPPER}
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <TreeView
              sx={ticketsTablesStyle.tree}
              defaultCollapseIcon={<ArrowDownIcon style={ticketsTablesStyle.arrowTop} />}
              defaultExpandIcon={<ArrowDownIcon />}
            >
              <TreeItem
                nodeId="1"
                label={
                  <Box>
                    <Box sx={{fontSize: '14px', marginLeft: '4px'}}>Advanced Settings</Box>
                  </Box>
                }
              >
                <TreeItem
                  nodeId="2"
                  label={
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '12px',
                        padding: '8px 0',
                      }}
                    >
                      <Box sx={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                        <Field
                          id={`tableInfos[${index}].displayRemainingTickets`}
                          name={`tableInfos[${index}].displayRemainingTickets`}
                          component={Switch}
                          sx={{marginLeft: '12px'}}
                          isArrayData
                          checked={table.displayRemainingTickets}
                        />
                        <Typography sx={{fontSize: '14px'}}>Display remaining quantity avaliable</Typography>
                      </Box>

                      <Box sx={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                        <Typography sx={{marginLeft: '12px', fontSize: '14px'}}>
                          Only display when quantity is fewer than
                        </Typography>
                        <Box>
                          <Field
                            id={`tableInfos[${index}].ticketsRemaining`}
                            name={`tableInfos[${index}].ticketsRemaining`}
                            component={Input}
                            type={NUMBER}
                            sx={{width: '100px'}}
                            placeholder="0"
                            isArrayData
                            disabled={!table.displayRemainingTickets}
                            inputProps={{
                              inputMode: 'numeric',
                              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                e.target.value = e.target.value.replace(/[^0-9]/g, '');
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  }
                />
                <TreeItem
                  nodeId="3"
                  label={
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: '4px', width: '100%'}}>
                      <Box sx={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                        <Field
                          id={`tableInfos[${index}].isTicketsVisibleOnAuction`}
                          name={`tableInfos[${index}].isTicketsVisibleOnAuction`}
                          component={Switch}
                          sx={{marginLeft: '12px'}}
                          isArrayData
                          checked={!table.isTicketsVisibleOnAuction}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            setFieldValue(`tableInfos[${index}].isTicketsVisibleOnAuction`, !e.target.checked);
                          }}
                        />

                        <Typography sx={{fontSize: '14px'}}>
                          Private Package: Private Packages are only accessed by a special URL and are not publicly
                          listed.
                        </Typography>
                      </Box>

                      {table.isTicketsVisibleOnAuction === false && (
                        <Box sx={{display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '64px'}}>
                          {table?.privatePurchaseUrl ? (
                            <>
                              <Typography
                                sx={{
                                  fontSize: '13px',
                                  color: 'text.secondary',
                                  wordBreak: 'break-all',
                                }}
                              >
                                {table.privatePurchaseUrl}
                              </Typography>

                              <Tooltip title={copiedId === table.id ? 'Copied!' : 'Copy link'}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(table.privatePurchaseUrl || '');
                                    setCopiedId(table.id);
                                    setTimeout(() => setCopiedId(null), 1500);
                                  }}
                                >
                                  <Typography sx={{fontSize: '14px'}}>{copiedId === table.id ? '✓' : '⧉'}</Typography>
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <Typography
                              sx={{
                                fontSize: '13px',
                                color: 'text.secondary',
                                fontStyle: 'italic',
                              }}
                            >
                              The link will be available after you save this event.
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  }
                />
              </TreeItem>
              {/* <TreeItem
                  nodeId="3"
                  label={
                    <Box sx={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                      <Field
                        id={`tableInfos[${index}].isTicketsVisibleOnAuction`}
                        name={`tableInfos[${index}].isTicketsVisibleOnAuction`}
                        component={Switch}
                        sx={{marginLeft: '12px'}}
                        isArrayData
                        checked={!table.isTicketsVisibleOnAuction}
                      />

                      <Typography sx={{fontSize: '14px'}}>
                        Private Package: Private Packages are only accessed by a special URL and are not publicly
                        listed.
                      </Typography>
                    </Box>
                  }
                />
              </TreeItem> */}
            </TreeView>
          </Box>
        ))}

        <Button
          title={ADD_TICKET_TIER}
          variant={TEXT}
          startIcon={<PlusIcon />}
          disableRipple
          sx={ticketsTablesStyle.button}
          onClick={addTableClickHandler}
        />
      </Box>
    </Box>
  );
};

export default TicketsTables;
